/*
 External links (defined as all links pointing to a domain that is not the
 same as the one from which the current document is being served)
 will have their 'target' attribute set to '_blank'.
 Social media sharing links will show in a popup.
 */
import {
  createElement,
  doesStringContainAny,
  getElement,
  getElements
} from './functional'

export function externalLinks () {
  const links = getElements('a')

  // If the link starts with any of these things, we'll open a 600x300 popup
  // window for them.
  const usePopupPrefixes = [
    'https://twitter.com/intent/tweet?',
    'https://www.facebook.com/sharer.php?',
    'https://www.linkedin.com/shareArticle?'
  ]

  const thisDomain =
    window.location.hostname.indexOf('www.') === 0
      ? window.location.hostname.substr(4)
      : window.location.hostname

  links.forEach(link => {
    const href = link.getAttribute('href')

    if (
      !href ||
      href.indexOf('mailto:') >= 0 ||
      doesStringContainAny(href, ['http://', 'https://', '//']) === false
    ) {
      return
    }

    const domain =
      link.hostname.indexOf('www.') === 0
        ? link.hostname.substr(4)
        : link.hostname

    if (domain !== thisDomain) {
      link.setAttribute('target', '_blank')
      // Add noopener and 'noreferrer' to work around this:
      // https://dev.to/ben/the-targetblank-vulnerability-by-example
      let rel = link.getAttribute('rel')

      if (!rel) {
        // will be null if it is not set
        rel = ''
      }

      link.setAttribute('rel', `${rel} noopener noreferrer`)
    }

    // Use a popup for social sharing links.
    for (const prefix of usePopupPrefixes) {
      if (href.indexOf(prefix) === 0) {
        link.addEventListener('click', event => {
          event.preventDefault()
          window.open(
            href,
            '_blank',
            'width=600,height=300,menubar=0,toolbar=0,status=0'
          )
        })

        break
      }
    }
  })
}

export function wrapTables ({
  elements = [],
  wrapClass = 'rte-RichText_Table'
} = {}) {
  elements.forEach(el => {
    const div = createElement('div', {
      class: wrapClass
    })
    div.innerHTML = el.outerHTML
    el.parentNode.insertBefore(el, div)
    el.remove()
  })
}

export function wrapIframes ({
  elements = [],
  wrapClass = 'rte-RichText_Iframe'
} = {}) {
  elements.forEach(el => {
    const div = createElement('div', {
      class: wrapClass
    })
    div.innerHTML = el.outerHTML
    el.parentNode.insertBefore(el, div)
    el.remove()

    // Re-set the src attribute on each iframe after page load
    // for Chrome's "incorrect iFrame content on 'back'" bug.
    // https://code.google.com/p/chromium/issues/detail?id=395791
    // Need to specifically target video and admin bar
    const newIframe = getElement('iframe', div)
    newIframe.setAttribute('src', newIframe.getAttribute('src'))
  })
}

/*
 Media breakpoints
 */
export const mediaBreakpoints = {
  xs: 480,
  sm: 768,
  md: 900,
  lg: 1200,
  xlg: 1440,
  xxlg: 1600
}

export function getOffsetTop (el, parent = document.body) {
  let offsetTop = 0

  do {
    if (!isNaN(el.offsetTop)) {
      offsetTop += el.offsetTop
    }

    el = el.offsetParent
  } while (el !== parent || el !== document.body)

  return offsetTop
}

export function getOffsetLeft (el, parent = document.body) {
  let offsetLeft = 0

  do {
    if (!isNaN(el.offsetLeft)) {
      offsetLeft += el.offsetLeft
    }

    el = el.offsetParent
  } while (el !== parent || el !== document.body)

  return offsetLeft
}

export function scrollToY (
  scrollTargetY = 0,
  speed = 300,
  easing = 'easeInOutQuint'
) {
  const scrollY = window.scrollY
  let currentTime = 0

  const time = Math.max(
    0.1,
    Math.min(Math.abs(scrollY - scrollTargetY) / speed, 0.8)
  )

  const easingEquations = {
    easeOutSine (pos) {
      return Math.sin(pos * (Math.PI / 2))
    },
    easeInOutSine (pos) {
      return -0.5 * (Math.cos(Math.PI * pos) - 1)
    },
    easeInOutQuint (pos) {
      if ((pos /= 0.5) < 1) {
        return 0.5 * Math.pow(pos, 5)
      }
      return 0.5 * (Math.pow(pos - 2, 5) + 2)
    }
  }

  function tick () {
    currentTime += 1 / 60

    const position = currentTime / time
    const t = easingEquations[easing](position)

    if (position < 1) {
      window.requestAnimationFrame(tick)

      window.scrollTo(0, scrollY + (scrollTargetY - scrollY) * t)
    } else {
      window.scrollTo(0, scrollTargetY)
    }
  }

  tick()
}

export function scrollToX (
  scrollTargetX = 0,
  speed = 300,
  easing = 'easeInOutQuint'
) {
  const scrollX = window.scrollX
  let currentTime = 0

  const time = Math.max(
    0.1,
    Math.min(Math.abs(scrollX - scrollTargetX) / speed, 0.8)
  )

  const easingEquations = {
    easeOutSine (pos) {
      return Math.sin(pos * (Math.PI / 2))
    },
    easeInOutSine (pos) {
      return -0.5 * (Math.cos(Math.PI * pos) - 1)
    },
    easeInOutQuint (pos) {
      if ((pos /= 0.5) < 1) {
        return 0.5 * Math.pow(pos, 5)
      }
      return 0.5 * (Math.pow(pos - 2, 5) + 2)
    }
  }

  function tick () {
    currentTime += 1 / 60

    const position = currentTime / time
    const t = easingEquations[easing](position)

    if (position < 1) {
      window.requestAnimationFrame(tick)

      window.scrollTo(scrollX + (scrollTargetX - scrollX) * t, 0)
    } else {
      window.scrollTo(scrollTargetX, 0)
    }
  }

  tick()
}

export class Cycler {
  constructor (items) {
    this.items = items
    this.currentPosition = 0
  }

  current () {
    return this.items[this.currentPosition]
  }

  set (val) {
    this.currentPosition = val
  }

  prev () {
    const current = this.current()
    this.currentPosition = this.currentPosition - 1

    if (this.currentPosition < 0) this.currentPosition = this.items.length - 1

    return current
  }

  next () {
    const current = this.current()
    this.currentPosition = (this.currentPosition + 1) % this.items.length

    return current
  }
}

export function debounce (func, wait, immediate) {
  let timeout

  return function () {
    const context = this
    const args = arguments

    const later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }

    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

export function slideDown (
  el,
  end,
  { start = 0, easing = 'easeInOutQuint', speed = 300 } = {}
) {
  let currentPosition = 0
  const time = Math.max(0.1, Math.min(Math.abs(end) / speed, 0.8))
  const easingEquations = {
    easeOutSine (pos) {
      return Math.sin(pos * (Math.PI / 2))
    },
    easeInOutSine (pos) {
      return -0.5 * (Math.cos(Math.PI * pos) - 1)
    },
    easeInOutQuint (pos) {
      if ((pos /= 0.5) < 1) {
        return 0.5 * Math.pow(pos, 5)
      }
      return 0.5 * (Math.pow(pos - 2, 5) + 2)
    }
  }

  return new Promise((resolve, reject) => {
    const tick = () => {
      currentPosition += 1 / 60
      const position = currentPosition / time
      const t = easingEquations[easing](position)

      if (position < 1) {
        window.requestAnimationFrame(tick)

        el.style.height = `${currentPosition + (end - currentPosition) * t}px`
      } else {
        el.style.height = `${end}px`

        resolve()
      }
    }

    tick()
  })
}

export function slideUp (
  el,
  start,
  { end = 0, easing = 'easeInOutQuint', speed = 300 } = {}
) {
  let currentPosition = 0
  const time = Math.max(0.1, Math.min(Math.abs(start) / speed, 0.8))
  const easingEquations = {
    easeOutSine (t) {
      return Math.sin(t * (Math.PI / 2))
    },
    easeInOutSine (t) {
      return -0.5 * (Math.cos(Math.PI * t) - 1)
    },
    easeInOutQuint (t) {
      return (t /= 0.5) < 1
        ? 0.5 * Math.pow(t, 5)
        : 0.5 * (Math.pow(t - 2, 5) + 2)
    }
  }

  return new Promise((resolve, reject) => {
    const tick = () => {
      currentPosition += 1 / 60
      const position = currentPosition / time
      const t = easingEquations[easing](position)

      if (position < 1) {
        window.requestAnimationFrame(tick)

        el.style.height = `${start - (start - end) * t}px`
      } else {
        el.style.height = `${end}px`

        resolve()
      }
    }

    tick()
  })
}
